import { useEffect, useRef, useState, type ReactNode } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { AnimatePresence, motion } from 'framer-motion'
import { ChevronDown, LogOut, Menu, X } from 'lucide-react'
import { AfosLogo } from './Logo'
import { OrbitField } from './OrbitField'
import { useAuth } from '../lib/auth'
import {
  absoluteSigninUrl,
  absoluteSignupUrl,
  resolveHostMode,
} from '../lib/hostMode'
import { cn } from '../lib/utils'

export type AppNavItem = {
  to: string
  label: string
  tone?: 'cyan' | 'amber' | 'foam'
  adminOnly?: boolean
}

export type AppNavGroup = {
  id: string
  label: string
  items: AppNavItem[]
}

/** The authenticated app's navigation, grouped by what a person is doing. */
export const NAV_GROUPS: AppNavGroup[] = [
  {
    id: 'operate',
    label: 'Operate',
    items: [
      { to: '/app', label: 'Command center' },
      { to: '/approvals', label: 'Approvals', tone: 'amber' },
      { to: '/cases', label: 'Cases', tone: 'cyan' },
      { to: '/tickets', label: 'Tickets' },
      { to: '/squads', label: 'Squads', tone: 'amber' },
    ],
  },
  {
    id: 'build',
    label: 'Build',
    items: [
      { to: '/product', label: 'Product', tone: 'cyan' },
      { to: '/shop', label: 'Shop', tone: 'amber' },
      { to: '/workforce', label: 'Workforce' },
      { to: '/ephemeral', label: 'Ephemeral', tone: 'amber' },
      { to: '/interview', label: 'Interview' },
      { to: '/ralph', label: 'Ralph', tone: 'amber' },
    ],
  },
  {
    id: 'govern',
    label: 'Govern',
    items: [
      { to: '/teams', label: 'Teams', tone: 'cyan' },
      { to: '/skills', label: 'Skills' },
      { to: '/learning', label: 'Learning', tone: 'amber' },
      { to: '/evaluations', label: 'Evaluations' },
      { to: '/engineering', label: 'Engineering', tone: 'cyan' },
    ],
  },
  {
    id: 'admin',
    label: 'Admin',
    items: [
      { to: '/enterprise', label: 'Enterprise' },
      { to: '/admin', label: 'Admin', adminOnly: true },
    ],
  },
]

const DEFAULT_NAV: AppNavItem[] = NAV_GROUPS.flatMap((g) => g.items)

function isActive(pathname: string, to: string): boolean {
  return pathname === to || (to !== '/app' && pathname.startsWith(to))
}

type Props = {
  children: ReactNode
  /** When false, hide OrbitField (rare). */
  orbit?: boolean
  nav?: AppNavItem[]
  trailing?: ReactNode
  /** Compact marketing chrome (no auth drawer). */
  marketing?: boolean
}

export function AppChrome({
  children,
  orbit = true,
  nav,
  trailing,
  marketing = false,
}: Props) {
  const { user, signOut } = useAuth()
  const location = useLocation()
  const [open, setOpen] = useState(false)
  const host = resolveHostMode()
  const signupHref = absoluteSignupUrl(host, 'com')
  const signinHref = absoluteSigninUrl(host, 'com')
  const externalApp = signupHref.startsWith('http')

  useEffect(() => {
    setOpen(false)
  }, [location.pathname])

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open])

  const canAdmin =
    !!user?.is_super_admin || !!user?.permissions?.includes('users.manage')
  const allowed = (item: AppNavItem) => !item.adminOnly || canAdmin
  const groups: AppNavGroup[] = nav
    ? [{ id: 'custom', label: 'Menu', items: nav.filter(allowed) }]
    : NAV_GROUPS.map((g) => ({ ...g, items: g.items.filter(allowed) })).filter(
        (g) => g.items.length > 0,
      )
  const items = nav ? nav.filter(allowed) : DEFAULT_NAV.filter(allowed)

  return (
    <div className="afos-shell relative">
      {orbit && <OrbitField />}
      <header className="relative z-30 border-b border-[var(--color-line)] bg-black/25 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-3 px-4 py-3 sm:px-6 sm:py-4">
          <Link to={marketing ? '/' : '/app'} className="min-w-0 shrink">
            <AfosLogo size={30} className="max-[380px]:gap-2" />
          </Link>

          {!marketing && (
            <nav aria-label="Primary" className="hidden items-center gap-1 lg:flex">
              {nav
                ? items.map((item) => (
                    <NavChip
                      key={item.to}
                      item={item}
                      active={isActive(location.pathname, item.to)}
                    />
                  ))
                : groups.map((group) => (
                    <NavGroupMenu
                      key={group.id}
                      group={group}
                      pathname={location.pathname}
                    />
                  ))}
            </nav>
          )}

          <div className="flex items-center gap-2 sm:gap-3">
            {trailing}
            {marketing ? (
              <>
                {externalApp ? (
                  <a
                    href={signinHref}
                    className="hidden rounded-full border border-[var(--color-line)] px-3 py-2 text-sm text-[var(--color-foam)] sm:inline-flex"
                  >
                    Sign in
                  </a>
                ) : (
                  <Link
                    to={signinHref}
                    className="hidden rounded-full border border-[var(--color-line)] px-3 py-2 text-sm text-[var(--color-foam)] sm:inline-flex"
                  >
                    Sign in
                  </Link>
                )}
                {externalApp ? (
                  <a
                    href={signupHref}
                    className="rounded-full bg-[var(--color-cyan)] px-3 py-2 text-sm font-semibold text-[var(--color-void)] sm:px-4"
                  >
                    Start free
                  </a>
                ) : (
                  <Link
                    to={signupHref}
                    className="rounded-full bg-[var(--color-cyan)] px-3 py-2 text-sm font-semibold text-[var(--color-void)] sm:px-4"
                  >
                    Start free
                  </Link>
                )}
              </>
            ) : (
              user && (
                <div className="hidden items-center gap-2 rounded-full border border-[var(--color-line)] bg-white/5 py-1 pl-1 pr-2 sm:flex sm:pr-3">
                  {user.picture ? (
                    <img src={user.picture} alt="" className="h-8 w-8 rounded-full" />
                  ) : (
                    <div className="flex h-8 w-8 items-center justify-center rounded-full bg-[var(--color-cyan)] text-xs font-bold text-[var(--color-void)]">
                      {user.name.slice(0, 1).toUpperCase()}
                    </div>
                  )}
                  <div className="hidden leading-tight md:block">
                    <div className="max-w-[10rem] truncate text-sm font-semibold">
                      {user.name}
                    </div>
                    <div className="text-[10px] uppercase tracking-wider text-[var(--color-mist)]">
                      {user.provider} sso
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => void signOut()}
                    aria-label="Sign out"
                    className="rounded-full p-1.5 text-[var(--color-mist)] hover:text-[var(--color-foam)]"
                  >
                    <LogOut className="h-4 w-4" />
                  </button>
                </div>
              )
            )}

            {!marketing && (
              <button
                type="button"
                className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-[var(--color-line)] text-[var(--color-foam)] lg:hidden"
                aria-label={open ? 'Close menu' : 'Open menu'}
                aria-expanded={open}
                onClick={() => setOpen((v) => !v)}
              >
                {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
              </button>
            )}
            {marketing && (
              <button
                type="button"
                className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-[var(--color-line)] text-[var(--color-foam)] sm:hidden"
                aria-label={open ? 'Close menu' : 'Open menu'}
                aria-expanded={open}
                onClick={() => setOpen((v) => !v)}
              >
                {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
              </button>
            )}
          </div>
        </div>

        <AnimatePresence>
          {open && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className={cn(
                'overflow-hidden border-t border-[var(--color-line)] bg-black/55 backdrop-blur-xl',
                marketing ? 'sm:hidden' : 'lg:hidden',
              )}
            >
              <div className="mx-auto flex max-w-7xl flex-col gap-1 px-4 py-3 sm:px-6">
                {marketing ? (
                  <>
                    {externalApp ? (
                      <a
                        className="rounded-2xl px-3 py-3 text-[var(--color-foam)]"
                        href={signinHref}
                      >
                        Sign in
                      </a>
                    ) : (
                      <Link
                        className="rounded-2xl px-3 py-3 text-[var(--color-foam)]"
                        to={signinHref}
                      >
                        Sign in
                      </Link>
                    )}
                    {externalApp ? (
                      <a
                        className="rounded-2xl bg-[var(--color-cyan)] px-3 py-3 text-center font-semibold text-[var(--color-void)]"
                        href={signupHref}
                      >
                        Start free trial
                      </a>
                    ) : (
                      <Link
                        className="rounded-2xl bg-[var(--color-cyan)] px-3 py-3 text-center font-semibold text-[var(--color-void)]"
                        to={signupHref}
                      >
                        Start free trial
                      </Link>
                    )}
                    <a className="rounded-2xl px-3 py-3 text-[var(--color-mist)]" href="#features">
                      Features
                    </a>
                    <a className="rounded-2xl px-3 py-3 text-[var(--color-mist)]" href="#workforce">
                      Workforce
                    </a>
                  </>
                ) : (
                  <>
                    {groups.map((group) => (
                      <div key={group.id} className="py-1">
                        {groups.length > 1 && (
                          <div className="px-3 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-[var(--color-mist)]">
                            {group.label}
                          </div>
                        )}
                        {group.items.map((item) => (
                          <Link
                            key={item.to}
                            to={item.to}
                            aria-current={isActive(location.pathname, item.to) ? 'page' : undefined}
                            className={cn(
                              'block rounded-2xl px-3 py-3 text-base',
                              isActive(location.pathname, item.to)
                                ? 'bg-white/10 text-[var(--color-cyan)]'
                                : 'text-[var(--color-foam)]',
                            )}
                          >
                            {item.label}
                          </Link>
                        ))}
                      </div>
                    ))}
                    {user && (
                      <button
                        type="button"
                        className="rounded-2xl px-3 py-3 text-left text-[var(--color-mist)]"
                        onClick={() => void signOut()}
                      >
                        Sign out · {user.email}
                      </button>
                    )}
                  </>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </header>
      {children}
    </div>
  )
}

function NavChip({ item, active }: { item: AppNavItem; active: boolean }) {
  const tone =
    item.tone === 'amber'
      ? 'text-[var(--color-amber)]'
      : 'text-[var(--color-cyan)]'
  return (
    <Link
      to={item.to}
      aria-current={active ? 'page' : undefined}
      className={cn(
        'rounded-full px-3 py-2 text-sm transition',
        active
          ? cn('border border-[var(--color-line)] bg-white/10', tone)
          : 'text-[var(--color-mist)] hover:text-[var(--color-foam)]',
      )}
    >
      {item.label}
    </Link>
  )
}

function NavGroupMenu({ group, pathname }: { group: AppNavGroup; pathname: string }) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const active = group.items.some((item) => isActive(pathname, item.to))
  const menuId = `nav-${group.id}`

  useEffect(() => {
    setOpen(false)
  }, [pathname])

  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    window.addEventListener('mousedown', onDown)
    window.addEventListener('keydown', onKey)
    return () => {
      window.removeEventListener('mousedown', onDown)
      window.removeEventListener('keydown', onKey)
    }
  }, [open])

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        aria-haspopup="true"
        aria-expanded={open}
        aria-controls={menuId}
        onClick={() => setOpen((v) => !v)}
        className={cn(
          'inline-flex items-center gap-1 rounded-full px-3 py-2 text-sm transition',
          active
            ? 'border border-[var(--color-line)] bg-white/10 text-[var(--color-cyan)]'
            : 'text-[var(--color-mist)] hover:text-[var(--color-foam)]',
        )}
      >
        {group.label}
        <ChevronDown
          aria-hidden="true"
          className={cn('h-3.5 w-3.5 transition', open && 'rotate-180')}
        />
      </button>
      {open && (
        <div
          id={menuId}
          className="glass absolute left-0 top-full z-40 mt-2 min-w-[13rem] rounded-2xl p-1.5 shadow-2xl"
        >
          {group.items.map((item) => {
            const on = isActive(pathname, item.to)
            return (
              <Link
                key={item.to}
                to={item.to}
                aria-current={on ? 'page' : undefined}
                className={cn(
                  'block rounded-xl px-3 py-2 text-sm transition',
                  on
                    ? 'bg-white/10 text-[var(--color-cyan)]'
                    : 'text-[var(--color-foam)] hover:bg-white/5',
                )}
              >
                {item.label}
              </Link>
            )
          })}
        </div>
      )}
    </div>
  )
}
